import { ActionIcon, Box, Button, Group, MultiSelect, Select, Stack, Table, Text, TextInput } from '@mantine/core';
import { IconPlus, IconRuler2, IconTrash } from '@tabler/icons-react';
import {
  calcDiscountedTotals,
  calcLine,
  formatMoney,
  MEASURE_METRIC_INFO,
  MEASURE_METRICS,
  measuredQuantity,
  QUANTITY_SCALE,
  type LineInput,
  type MeasureMetric,
  type MeasureRoom,
} from '@erp/shared';
import type { UnitOfMeasure, VatRate } from '../api/catalogs';
import type { PriceListItem } from '../api/priceList';
import { QuantityCalculatorPopover } from './QuantityCalculatorPopover';

export interface EstimateLineRow {
  key: string;
  priceListItemId: string | null;
  sectionId: string | null;
  sectionName: string;
  name: string;
  code: string | null;
  unitId: string;
  quantity: string;
  /** Задан — объём берётся из обмеров, поле quantity не используется. */
  measureMetric: MeasureMetric | null;
  /** Пусто — по всем помещениям. */
  measureRoomIds: string[];
  wastePercent: string;
  price: string;
  vatRateId: string;
}

export function emptyEstimateLineRow(): EstimateLineRow {
  return {
    key: crypto.randomUUID(),
    priceListItemId: null,
    sectionId: null,
    sectionName: '',
    name: '',
    code: '',
    unitId: '',
    quantity: '1',
    measureMetric: null,
    measureRoomIds: [],
    wastePercent: '',
    price: '',
    vatRateId: '',
  };
}

/**
 * Объём строки: по обмерам — той же функцией, что пересчитывает сервер,
 * иначе — как ввёл пользователь.
 */
export function effectiveQuantity(row: EstimateLineRow, rooms: MeasureRoom[]): string {
  if (!row.measureMetric) return row.quantity;
  return measuredQuantity({
    metric: row.measureMetric,
    rooms,
    roomIds: row.measureRoomIds,
    wastePercent: row.wastePercent,
  }).toFixed(QUANTITY_SCALE);
}

interface Props {
  lines: EstimateLineRow[];
  onChange: (lines: EstimateLineRow[]) => void;
  canEdit: boolean;
  priceListItemOptions: PriceListItem[];
  units: UnitOfMeasure[];
  vatRates: VatRate[];
  rooms: MeasureRoom[];
  /** Скидка заказчику, % — как введена в поле. */
  discountPercent: string;
  onDiscountPercentChange: (value: string) => void;
}

const metricOptions = MEASURE_METRICS.map((m) => ({
  value: m,
  label: `${MEASURE_METRIC_INFO[m].label}, ${MEASURE_METRIC_INFO[m].unit}`,
}));

/** Строка для расчёта сумм; null — объём или цена ещё не введены. */
function lineInput(row: EstimateLineRow, vatRates: VatRate[], rooms: MeasureRoom[]): LineInput | null {
  const quantity = effectiveQuantity(row, rooms).trim().replace(',', '.');
  const price = row.price.trim().replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(quantity) || !/^\d+(\.\d+)?$/.test(price)) return null;
  const vatRate = vatRates.find((r) => r.id === row.vatRateId);
  return { quantity, price, vatRateValue: vatRate?.rate ?? null };
}

/** Строка = ноль сумм, чтобы не показывать NaN, пока поля не заполнены. */
function calcRowAmounts(row: EstimateLineRow, vatRates: VatRate[], rooms: MeasureRoom[]) {
  const input = lineInput(row, vatRates, rooms);
  if (!input) return { amount: '0.00', vatAmount: '0.00', totalAmount: '0.00' };
  const amounts = calcLine(input);
  return {
    amount: amounts.amount.toFixed(2),
    vatAmount: amounts.vatAmount.toFixed(2),
    totalAmount: amounts.totalAmount.toFixed(2),
  };
}

/**
 * Редактор строк сметы. Объём (quantity) × цена = сумма, как в акте.
 * Раздел (sectionName) подставляется из выбранной расценки, но остаётся
 * редактируемым текстовым полем — печатная форма группирует строки по нему.
 */
/**
 * Скидка из поля ввода: пусто — без скидки (null), неверное значение —
 * undefined. Запятая допустима, не больше двух знаков после неё — как в базе.
 */
export function parseDiscountPercent(value: string): string | null | undefined {
  const v = value.trim().replace(',', '.');
  if (v === '') return null;
  if (!/^\d+(\.\d{1,2})?$/.test(v) || Number(v) > 100) return undefined;
  return v;
}

export function EstimateLinesEditor({
  lines,
  onChange,
  canEdit,
  priceListItemOptions,
  units,
  vatRates,
  rooms,
  discountPercent,
  onDiscountPercentChange,
}: Props) {
  const roomOptions = rooms.map((r) => ({ value: r.id, label: r.name || 'Без названия' }));
  const priceListSelectOptions = priceListItemOptions.map((i) => ({
    value: i.id,
    label: i.section ? `${i.section.name} / ${i.name}` : i.name,
  }));
  const unitOptions = units.map((u) => ({ value: u.id, label: u.symbol }));
  const vatRateOptions = vatRates.map((r) => ({ value: r.id, label: r.name }));

  function updateRow(key: string, patch: Partial<EstimateLineRow>) {
    onChange(lines.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  function pickPriceListItem(key: string, priceListItemId: string | null) {
    const item = priceListItemOptions.find((i) => i.id === priceListItemId);
    if (!item) {
      updateRow(key, { priceListItemId: null });
      return;
    }
    updateRow(key, {
      priceListItemId: item.id,
      sectionId: item.sectionId,
      sectionName: item.section?.name ?? '',
      name: item.name,
      code: item.code,
      unitId: item.unitId,
      price: item.price,
      vatRateId: item.vatRateId ?? '',
      // Показатель из расценки включаем, только когда обмеры уже есть: иначе
      // строка получила бы нулевой объём, и смета без обмеров не сохранилась бы.
      ...(item.measureMetric && rooms.length > 0
        ? { measureMetric: item.measureMetric, measureRoomIds: [], wastePercent: item.wastePercent ?? '' }
        : {}),
    });
  }

  function addRow() {
    onChange([...lines, emptyEstimateLineRow()]);
  }

  function removeRow(key: string) {
    onChange(lines.filter((l) => l.key !== key));
  }

  const rowAmounts = lines.map((row) => calcRowAmounts(row, vatRates, rooms));
  // Итоги — той же функцией, что на сервере: скидка применяется к каждой
  // строке и НДС округляется построчно, иначе итог на экране разошёлся бы
  // с сохранённой сметой на копейки.
  const discount = parseDiscountPercent(discountPercent);
  const totals = calcDiscountedTotals(
    lines.flatMap((row) => {
      const input = lineInput(row, vatRates, rooms);
      return input ? [input] : [];
    }),
    discount ?? null,
  );
  const hasDiscount = !totals.discountAmount.isZero();

  return (
    <Box>
      <Box style={{ overflowX: 'auto' }}>
        <Table verticalSpacing={6} miw={1440}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={40}>№</Table.Th>
              <Table.Th w={220}>Из расценок</Table.Th>
              <Table.Th w={140}>Раздел</Table.Th>
              <Table.Th>Наименование работ</Table.Th>
              <Table.Th w={110}>Ед.</Table.Th>
              <Table.Th w={200}>По обмерам</Table.Th>
              <Table.Th w={140}>Объём</Table.Th>
              <Table.Th w={120}>Цена</Table.Th>
              <Table.Th w={150}>Ставка НДС</Table.Th>
              <Table.Th w={110}>Сумма</Table.Th>
              <Table.Th w={100}>НДС</Table.Th>
              <Table.Th w={110}>Всего</Table.Th>
              <Table.Th w={40} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {lines.map((row, index) => {
              const amounts = rowAmounts[index]!;
              return (
                <Table.Tr key={row.key}>
                  <Table.Td>{index + 1}</Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      placeholder="Выбрать"
                      data={priceListSelectOptions}
                      value={row.priceListItemId}
                      onChange={(v) => pickPriceListItem(row.key, v)}
                      disabled={!canEdit}
                      searchable
                      clearable
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      value={row.sectionName}
                      onChange={(e) => updateRow(row.key, { sectionName: e.currentTarget.value })}
                      disabled={!canEdit}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      value={row.name}
                      onChange={(e) => updateRow(row.key, { name: e.currentTarget.value })}
                      disabled={!canEdit}
                      required
                    />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      placeholder="—"
                      data={unitOptions}
                      value={row.unitId || null}
                      onChange={(v) => updateRow(row.key, { unitId: v ?? '' })}
                      disabled={!canEdit}
                      searchable
                      clearable
                    />
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={4}>
                      <Select
                        size="xs"
                        placeholder={rooms.length > 0 ? 'Вручную' : 'Нет обмеров'}
                        data={metricOptions}
                        value={row.measureMetric}
                        onChange={(v) =>
                          updateRow(
                            row.key,
                            v
                              ? { measureMetric: v as MeasureMetric }
                              : // Отключили обмеры — оставляем посчитанный объём как
                                // ручной, чтобы его можно было поправить, а не набирать заново.
                                { measureMetric: null, quantity: effectiveQuantity(row, rooms) },
                          )
                        }
                        disabled={!canEdit || (rooms.length === 0 && !row.measureMetric)}
                        clearable
                        leftSection={<IconRuler2 size={14} />}
                        comboboxProps={{ width: 300 }}
                      />
                      {row.measureMetric && (
                        <Group gap={4} wrap="nowrap">
                          <MultiSelect
                            size="xs"
                            placeholder="Все помещения"
                            data={roomOptions}
                            value={row.measureRoomIds}
                            onChange={(ids) => updateRow(row.key, { measureRoomIds: ids })}
                            disabled={!canEdit}
                            style={{ flex: 1 }}
                          />
                          <TextInput
                            size="xs"
                            w={56}
                            placeholder="0"
                            rightSection={<Text size="xs">%</Text>}
                            title="Запас на подрезку и отходы"
                            inputMode="decimal"
                            value={row.wastePercent}
                            onChange={(e) => updateRow(row.key, { wastePercent: e.currentTarget.value })}
                            disabled={!canEdit}
                          />
                        </Group>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    {row.measureMetric ? (
                      <TextInput
                        size="xs"
                        value={effectiveQuantity(row, rooms)}
                        readOnly
                        variant="filled"
                        title="Считается по обмерам"
                      />
                    ) : (
                      <Group gap={2} wrap="nowrap">
                        <TextInput
                          size="xs"
                          value={row.quantity}
                          onChange={(e) => updateRow(row.key, { quantity: e.currentTarget.value })}
                          disabled={!canEdit}
                          style={{ flex: 1 }}
                        />
                        <QuantityCalculatorPopover
                          disabled={!canEdit}
                          onApply={(quantity) => updateRow(row.key, { quantity })}
                        />
                      </Group>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      value={row.price}
                      onChange={(e) => updateRow(row.key, { price: e.currentTarget.value })}
                      disabled={!canEdit}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      data={vatRateOptions}
                      value={row.vatRateId || null}
                      onChange={(v) => updateRow(row.key, { vatRateId: v ?? '' })}
                      disabled={!canEdit}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{formatMoney(amounts.amount)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs">{formatMoney(amounts.vatAmount)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" fw={500}>
                      {formatMoney(amounts.totalAmount)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    {canEdit && (
                      <ActionIcon variant="subtle" color="red" onClick={() => removeRow(row.key)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    )}
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Box>

      {canEdit && (
        <Button mt="xs" size="xs" variant="light" leftSection={<IconPlus size={14} />} onClick={addRow}>
          Добавить строку
        </Button>
      )}

      <Stack mt="sm" gap={2} align="flex-end">
        <TextInput
          label="Скидка, %"
          placeholder="нет"
          inputMode="decimal"
          value={discountPercent}
          onChange={(e) => onDiscountPercentChange(e.currentTarget.value)}
          disabled={!canEdit}
          error={discount === undefined ? 'От 0 до 100' : undefined}
          w={120}
          size="xs"
          styles={{ input: { textAlign: 'right' } }}
        />
        {hasDiscount && (
          <>
            <Text size="sm">Итого без скидки: {formatMoney(totals.totalBeforeDiscount)}</Text>
            <Text size="sm">
              Скидка {discountPercent.trim()} %: −{formatMoney(totals.discountAmount)}
            </Text>
          </>
        )}
        <Text size="sm">Сумма без НДС: {formatMoney(totals.subtotal)}</Text>
        <Text size="sm">НДС: {formatMoney(totals.vatAmount)}</Text>
        <Text size="md" fw={700}>
          {hasDiscount ? 'Итого со скидкой' : 'Итого'}: {formatMoney(totals.total)}
        </Text>
      </Stack>
    </Box>
  );
}
